import { Action, Reducer, ActionCreator, combineReducers } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { batch } from 'react-redux';
import { DataManager, Query } from '@syncfusion/ej2-data';
import moment from 'moment';

import { RootState } from '..';
import * as listGridStore from './listGrid.store';
import { ITrackingsListState, ITrackingsListFilterState } from '../../interfaces/states/ITrackingsListState';
import { ITracking } from '../../common/model/tracking.model';
import { ICustomFieldOptions } from '../../common/model/customFieldOptions.model';
import { IMyRule } from '../../common/model/myRule.model';
import { RuleCondition } from '../../common/model/enumerations/ruleCondition.model';
import { RuleOperator } from '../../common/model/enumerations/ruleOperator.model';
import { IQueryBuilderService, IODataService } from '../../services';
import * as dateHelpers from '../../helpers/date.helpers';
import { DATE_MOMENT_FORMAT } from '../../common/constants/advancedFilter.constants';
import { ITrackingsService } from '../../services';
import { getCurrentLanguage } from '../../i18n';

export enum Type {
  RESET = '@@trackingsList/RESET',
  RESET_HARD = '@@trackingsList/RESET_HARD',
  SET_TRACKINGS = '@@trackingsList/SET_REPORTS',
  SET_FILTER = '@@trackingsList/SET_FILTER',
  SET_FILTER_REPORT_NUMBER = '@@trackingsList/SET_FILTER_REPORT_NUMBER',
  SET_FILTER_VERSION = '@@trackingsList/SET_FILTER_VERSION',
  SET_FILTER_SELECTION_NUMBER = '@@trackingsList/SET_FILTER_SELECTIO_NUMBER',
  SET_FILTER_REFERENCE = '@@trackingsList/SET_FILTER_REFERENCE',
  SET_FILTER_MODEL = '@@trackingsList/SET_FILTER_MODEL',
  SET_FILTER_COMPANY_VENDOR_CODE = '@@trackingsList/SET_FILTER_COPMANY_VENDOR_CODE',
  SET_FILTER_VENDOR = '@@trackingsList/SET_FILTER_VENDOR',
  SET_FILTER_SEASON_CODE = '@@trackingsList/SET_FILTER_SEASON_CODE',
  SET_FILTER_OFFICE_CODE = '@@trackingsList/SET_FILTER_OFFICE_CODE',
  SET_SEASON_OPTIONS = '@@trackingsList/SET_SEASON_OPTIONS',
  SET_OFFICE_OPTIONS = '@@trackingsList/SET_OFFICE_OPTIONS',
  SET_IS_FETCHING = '@@trackingsList/SET_IS_FETCHING',
  SET_SEARCH_DATE = '@@trackingsList/SET_SEARCH_DATE',
  SET_CURRENT_PAGE = '@@trackingsList/SET_CURRENT_PAGE',
  SET_PAGE_SIZE = '@@trackingsList/SET_PAGE_SIZE',
  SET_TOTAL_COUNT = '@@trackingsList/SET_TOTAL_COUNT',
  SET_SORTING = '@@trackingsList/SET_SORTING',
  SET_FILTER_SEASON_CODES = '@@trackingsList/SET_FILTER_SEASON_CODES',
  SET_FILTER_OFFICE_CODES = '@@trackingsList/SET_FILTER_OFFICE_CODES',
  SET_FILTER_SHIPMENT_DATE = '@@trackingsList/SET_FILTER_SHIPMENT_DATE',
  SET_ARE_SETTINGS_LOADED = '@@trackingsList/SET_ARE_SETTINGS_LOADED'
}

const initialState: ITrackingsListState = {
  trackings: [],
  filter: {
    reportNumber: null,
    version: null,
    selectionNumber: null,
    reference: null,
    model: null,
    orderDocumentNumber: null,
    seasonCodes: [],
    officeCodes: [],
    shipmentDate: []
  },
  seasonOptions: [],
  officeOptions: [],
  grid: listGridStore.initialState,
  areOptionsLoaded: false
};

// Action

export type Actions = ResetAction | ResetHardAction | SetTrackingsAction | SetFilterAction
  | SetFilterReportNumberAction | SetFilterReferenceAction | SetFilterModelAction
  | SetSeasonOptionsAction | SetOfficeOptionsAction | listGridStore.SetIsFetchingAction
  | listGridStore.SetSearchDateAction | listGridStore.SetCurrentPageAction
  | listGridStore.SetPageSizeAction | listGridStore.SetTotalCountAction | listGridStore.SetSortingAction
  | SetFilterVersionAction | SetFilterSelectionNumberAction
  | SetFilterSeasonCodesAction | SetFilterOfficeCodesAction | SetFilterShipmentDateAction | SetAreSettingsLoadedAction;

export type ResetAction = Action<Type.RESET>

export type ResetHardAction = Action<Type.RESET_HARD>

export interface SetTrackingsAction extends Action<Type.SET_TRACKINGS> {
  trackings: ITracking[];
}

export interface SetFilterAction extends Action<Type.SET_FILTER> {
  filter: ITrackingsListFilterState;
}

export interface SetFilterReportNumberAction extends Action<Type.SET_FILTER_REPORT_NUMBER> {
  reportNumber: string;
}

export interface SetFilterVersionAction extends Action<Type.SET_FILTER_VERSION> {
  version: string;
}

export interface SetFilterSelectionNumberAction extends Action<Type.SET_FILTER_SELECTION_NUMBER> {
  selectionNumber: string;
}

export interface SetFilterReferenceAction extends Action<Type.SET_FILTER_REFERENCE> {
  reference: string;
}

export interface SetFilterModelAction extends Action<Type.SET_FILTER_MODEL> {
  model: string;
}

export interface SetSeasonOptionsAction extends Action<Type.SET_SEASON_OPTIONS> {
  seasonOptions: ICustomFieldOptions[];
}

export interface SetOfficeOptionsAction extends Action<Type.SET_OFFICE_OPTIONS> {
  officeOptions: ICustomFieldOptions[];
}

export interface SetFilterSeasonCodesAction extends Action<Type.SET_FILTER_SEASON_CODES> {
  seasonCodes: string[];
}

export interface SetFilterOfficeCodesAction extends Action<Type.SET_FILTER_OFFICE_CODES> {
  officeCodes: string[];
}

export interface SetFilterShipmentDateAction extends Action<Type.SET_FILTER_SHIPMENT_DATE> {
  shipmentDate: Date[];
}

export interface SetAreSettingsLoadedAction extends Action<Type.SET_ARE_SETTINGS_LOADED> {
  areSettingsLoaded: boolean;
}

export const reset: ActionCreator<ResetAction> = () => ({
  type: Type.RESET
});

export const resetHard: ActionCreator<ResetHardAction> = () => ({
  type: Type.RESET_HARD
});

export const setTrackings: ActionCreator<SetTrackingsAction> = (trackingsLocal: ITracking[]) => ({
  type: Type.SET_TRACKINGS,
  trackings: trackingsLocal
});

export const setFilter: ActionCreator<SetFilterAction> = (filterLocal: ITrackingsListFilterState) => ({
  type: Type.SET_FILTER,
  filter: filterLocal
});

export const setFilterReportNumber: ActionCreator<SetFilterReportNumberAction> = (reportNumber: string) => ({
  type: Type.SET_FILTER_REPORT_NUMBER,
  reportNumber
});

export const setFilterVersion: ActionCreator<SetFilterVersionAction> = (version: string) => ({
  type: Type.SET_FILTER_VERSION,
  version
});

export const setFilterSelectionNumber: ActionCreator<SetFilterSelectionNumberAction> = (selectionNumber: string) => ({
  type: Type.SET_FILTER_SELECTION_NUMBER,
  selectionNumber
});

export const setFilterReference: ActionCreator<SetFilterReferenceAction> = (reference: string) => ({
  type: Type.SET_FILTER_REFERENCE,
  reference
});

export const setFilterModel: ActionCreator<SetFilterModelAction> = (model: string) => ({
  type: Type.SET_FILTER_MODEL,
  model
});

export const setSeasonOptions: ActionCreator<SetSeasonOptionsAction> = (seasonOptionsLocal: ICustomFieldOptions[]) => ({
  type: Type.SET_SEASON_OPTIONS,
  seasonOptions: seasonOptionsLocal
});

export const setOfficeOptions: ActionCreator<SetOfficeOptionsAction> = (officeOptionsLocal: ICustomFieldOptions[]) => ({
  type: Type.SET_OFFICE_OPTIONS,
  officeOptions: officeOptionsLocal
});

export const setFilterSeasonCodes: ActionCreator<SetFilterSeasonCodesAction> = (seasonCodes: string[]) => ({
  type: Type.SET_FILTER_SEASON_CODES,
  seasonCodes
});

export const setFilterOfficeCodes: ActionCreator<SetFilterOfficeCodesAction> = (officeCodes: string[]) => ({
  type: Type.SET_FILTER_OFFICE_CODES,
  officeCodes
});

export const setFilterShipmentDate: ActionCreator<SetFilterShipmentDateAction> = (shipmentDate: Date[]) => ({
  type: Type.SET_FILTER_SHIPMENT_DATE,
  shipmentDate
});

export const setAreSettingsLoaded: ActionCreator<SetAreSettingsLoadedAction> = (areSettingsLoadedLocal: boolean) => ({
  type: Type.SET_ARE_SETTINGS_LOADED,
  areSettingsLoaded: areSettingsLoadedLocal
});

export const computeSimpleFilterRule = (filterLocal: ITrackingsListFilterState): IMyRule => {
  const myRule: IMyRule = {
    condition: RuleCondition.AND,
    myRules: []
  };
  if (filterLocal.reportNumber) {
    myRule.myRules.push({
      field: 'reportNumber',
      operator: RuleOperator.CONTAINS,
      value: filterLocal.reportNumber,
      ignoreCase: true
    });
  }
  if (filterLocal.version) {
    myRule.myRules.push({
      field: 'version',
      operator: RuleOperator.CONTAINS,
      value: filterLocal.version
    });
  }
  if (filterLocal.selectionNumber) {
    myRule.myRules.push({
      field: 'selectionNumber',
      operator: RuleOperator.CONTAINS,
      value: filterLocal.selectionNumber,
      ignoreCase: true
    });
  }
  if (filterLocal.reference) {
    myRule.myRules.push({
      field: 'reference',
      operator: RuleOperator.CONTAINS,
      value: filterLocal.reference,
      ignoreCase: true
    });
  }
  if (filterLocal.model) {
    myRule.myRules.push({
      field: 'model',
      operator: RuleOperator.CONTAINS,
      value: filterLocal.model,
      ignoreCase: true
    });
  }
  if (filterLocal.officeCodes) {
    myRule.myRules.push({
      field: 'office',
      operator: RuleOperator.IN,
      value: filterLocal.officeCodes
    });
  }
  if (filterLocal.seasonCodes) {
    myRule.myRules.push({
      field: 'seasonCode',
      operator: RuleOperator.IN,
      value: filterLocal.seasonCodes
    });
  }
  if (filterLocal.shipmentDate.length) {
    myRule.myRules.push({
      field: 'shipmentDate',
      operator: RuleOperator.GREATER_THAN_OR_EQUAL,
      value: dateHelpers.toDateWithoutZones(filterLocal.shipmentDate[0], DATE_MOMENT_FORMAT),
      isDate: true
    });
    myRule.myRules.push({
      field: 'shipmentDate',
      operator: RuleOperator.LESS_THAN_OR_EQUAL,
      value: dateHelpers.toDateWithoutZones(filterLocal.shipmentDate[1], DATE_MOMENT_FORMAT),
      isDate: true
    });
  }

  return myRule;
};

export const getAllFilterRules = (state: RootState): IMyRule => {
  let myRule: IMyRule = {
    condition: RuleCondition.AND,
    myRules: []
  };

  const filterLocal = state.trackingsListStore.filter;
  myRule = computeSimpleFilterRule(filterLocal);

  return myRule;
}

export const getInitialQuery = (): Query => {
  const query = new Query()
    .select([
      'internalTypeId', 'reportNumber', 'version', 'selectionNumber', 'seasonCode', 'date', 'description', 'reference', 'model', 'companyVendorCode', 'vendor', 'division', 'mark', 'office',
      'section', 'uneco', 'orderDocumentNumber', 'isVisible', 'shipmentDate'
    ]);

  return query;
}

export const fetchTrackings: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = (
  queryBuilderService: IQueryBuilderService,
  oDataService: IODataService,
  trackingsDataManager: DataManager
) => (
    async (dispatch, getState) => {
      dispatch(listGridStore.setIsFetching(Type.SET_IS_FETCHING, true));

      let query = getInitialQuery();

      const myRule = getAllFilterRules(getState());

      query = queryBuilderService.buildQuery(trackingsDataManager, myRule, query);

      const currentPageLocal = getState().trackingsListStore.grid.currentPage;
      const pageSizeLocal = getState().trackingsListStore.grid.pageSize;
      const sortFieldLocal = getState().trackingsListStore.grid.sortField;
      const sortDirectionLocal = getState().trackingsListStore.grid.sortDirection;

      query = query.page(currentPageLocal, pageSizeLocal);
      if (sortFieldLocal && sortDirectionLocal) {
        query = sortDirectionLocal === 'Ascending' ? query.sortBy(sortFieldLocal) : query.sortByDesc(sortFieldLocal);
      }
      const trackingsResponse = await oDataService.executeQueryWithCount<ITracking>(trackingsDataManager, query);
      if (trackingsResponse && trackingsResponse.result) {
        const trackingsLocal = trackingsResponse.result.map((tracking) => {
          return {
            ...tracking,
            date: tracking.date ? moment(tracking.date) : tracking.date,
            shipmentDate: tracking.shipmentDate ? moment(tracking.shipmentDate) : tracking.shipmentDate
          }
        });
        batch(() => {
          dispatch(setTrackings(trackingsLocal));
          dispatch(listGridStore.setSearchDate(Type.SET_SEARCH_DATE, moment()));
          dispatch(listGridStore.setTotalCount(Type.SET_TOTAL_COUNT, trackingsResponse.count));
        });
      }
      dispatch(listGridStore.setIsFetching(Type.SET_IS_FETCHING, false));
    }
);

export const fetchOptionsFieldSettings: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = (trackingsService: ITrackingsService) => (
  async (dispatch) => {
    batch(() => {
      dispatch(setSeasonOptions(initialState.seasonOptions));
      dispatch(setOfficeOptions(initialState.officeOptions));
    });

    batch(() => {
      dispatch(listGridStore.setIsFetching(Type.SET_IS_FETCHING, true));
      dispatch(setAreSettingsLoaded(initialState.areOptionsLoaded));
    });

    batch(async () => {
      dispatch(setSeasonOptions(await trackingsService.fetchSeasonOptions('CI', getCurrentLanguage(), true)));
      dispatch(setOfficeOptions(await trackingsService.fetchOfficeOptions('CI', false)));
      dispatch(setAreSettingsLoaded(true));
      dispatch(listGridStore.setIsFetching(Type.SET_IS_FETCHING, false));
    });
  }
);

// Reducers

const trackings: Reducer<ITracking[]> = (state = initialState.trackings, action) => {
  switch (action.type) {
    case Type.RESET:
      return state;
    case Type.RESET_HARD:
      return initialState.trackings;
    case Type.SET_TRACKINGS:
      return (action as SetTrackingsAction).trackings;
    default:
      return state;
  }
};

const filter: Reducer<ITrackingsListFilterState> = (state = initialState.filter, action) => {
  switch (action.type) {
    case Type.RESET:
      return state;
    case Type.RESET_HARD:
      return initialState.filter;
    case Type.SET_FILTER:
      return (action as SetFilterAction).filter;
    case Type.SET_FILTER_REPORT_NUMBER:
      return { ...state, reportNumber: (action as SetFilterReportNumberAction).reportNumber };
    case Type.SET_FILTER_VERSION:
      return { ...state, version: (action as SetFilterVersionAction).version };
    case Type.SET_FILTER_SELECTION_NUMBER:
      return { ...state, selectionNumber: (action as SetFilterSelectionNumberAction).selectionNumber };
    case Type.SET_FILTER_REFERENCE:
      return { ...state, reference: (action as SetFilterReferenceAction).reference };
    case Type.SET_FILTER_MODEL:
      return { ...state, model: (action as SetFilterModelAction).model };
    case Type.SET_FILTER_SEASON_CODES:
      return { ...state, seasonCodes: (action as SetFilterSeasonCodesAction).seasonCodes };
    case Type.SET_FILTER_OFFICE_CODES:
      return { ...state, officeCodes: (action as SetFilterOfficeCodesAction).officeCodes };
    case Type.SET_FILTER_SHIPMENT_DATE:
      return { ...state, shipmentDate: (action as SetFilterShipmentDateAction).shipmentDate };
    default:
      return state;
  }
};

const areOptionsLoaded: Reducer<boolean> = (state = initialState.areOptionsLoaded, action) => {
  switch (action.type) {
    case Type.RESET:
      return state;
    case Type.RESET_HARD:
      return initialState.areOptionsLoaded;
    case Type.SET_ARE_SETTINGS_LOADED:
      return (action as SetAreSettingsLoadedAction).areSettingsLoaded;
    default:
      return state;
  }
};

const seasonOptions: Reducer<ICustomFieldOptions[]> = (state = initialState.seasonOptions, action) => {
  switch (action.type) {
    case Type.RESET:
      return state;
    case Type.RESET_HARD:
      return initialState.seasonOptions;
    case Type.SET_SEASON_OPTIONS:
      return (action as SetSeasonOptionsAction).seasonOptions;
    default:
      return state;
  }
};

const officeOptions: Reducer<ICustomFieldOptions[]> = (state = initialState.officeOptions, action) => {
  switch (action.type) {
    case Type.RESET:
      return state;
    case Type.RESET_HARD:
      return initialState.officeOptions;
    case Type.SET_OFFICE_OPTIONS:
      return (action as SetOfficeOptionsAction).officeOptions;
    default:
      return state;
  }
};

export const trackingsListStore = combineReducers({
  trackings,
  filter,
  seasonOptions,
  officeOptions,
  areOptionsLoaded,
  grid: listGridStore.createListGridStore(Type, 'selectionNumber', 'Ascending'),
});
